import { el } from '@/ui/dom';

/**
 * The "Sign in with Google" button, built once and used at both entry points.
 *
 * There are exactly two places a user can start the handshake — the sign-in wall in
 * `SignInScreen.ts` and the account dialog in `src/ui/AccountMenu.ts` — and before this component
 * they disagreed with each other: the wall drew a dark disc with a letter G in it, and the dialog
 * used the generic dialog button with a folder icon. Two lookalikes are a worse outcome than one,
 * and a fabricated mark is specifically the thing Google's identity guidelines exist to prevent.
 *
 * So the mark below is the real one, drawn from Google's published four-colour geometry, and the
 * button around it follows their light-theme specification: white surface, 1px #747775 outline,
 * Roboto Medium, and the logo given its own clear space on the leading edge. The palette is
 * hard-coded rather than themed. Nothing here should ever inherit a colour from the surrounding
 * UI — which is why the mark deliberately does not live in `src/ui/Icons.ts`, whose glyphs are all
 * drawn in `currentColor` and recoloured freely by whatever panel they land in.
 */

/**
 * Google's four-colour "G", inline rather than fetched.
 *
 * It renders on the sign-in wall, which is the one screen that must come up correctly on a cold
 * cache before any of the app's own assets exist — a logo that arrives over the network is a logo
 * that is sometimes missing, and a sign-in button with a hole where the mark should be looks like
 * a phishing page. `aria-hidden` because the button's own text already names Google; a screen
 * reader announcing "Google logo, Sign in with Google" says it twice.
 */
export const GOOGLE_MARK_SVG = `
<svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.28-3.14.76-4.59l-7.97-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
</svg>`;

export interface GoogleSignInButtonOptions {
  /** Called once, on the first click. The button disables itself before this runs. */
  onSignIn: () => void;
  /**
   * One of Google's approved strings. Defaults to "Sign in with Google", which is the correct one
   * for a wall you cannot get past; "Continue with Google" reads better where signing in is an
   * upgrade rather than a requirement.
   */
  label?: string;
  /** Shown while the redirect is in flight. The mark stays; only this text is swapped. */
  pendingLabel?: string;
  /** `large` scales the whole button proportionally, for use as a page's single action. */
  size?: 'standard' | 'large';
  /** Stretch to the width of the container, up to the button's own maximum. */
  block?: boolean;
}

/**
 * Build the button.
 *
 * The pending state swaps the label alone. The obvious implementation — assigning `textContent` on
 * the button — throws the logo away at the exact moment the user is looking to see whether they
 * pressed the right thing, which is when a sign-in button most needs to still look like itself.
 */
export function googleSignInButton(options: GoogleSignInButtonOptions): HTMLButtonElement {
  const label = options.label ?? 'Sign in with Google';
  const pendingLabel = options.pendingLabel ?? 'Redirecting to Google…';

  const text = el('span', { class: 'lg-gsi__label', text: label });
  const classes = ['lg-gsi'];
  if (options.size === 'large') classes.push('lg-gsi--large');
  if (options.block === true) classes.push('lg-gsi--block');

  const button = el('button', {
    class: classes.join(' '),
    // `data-autofocus` keeps the account dialog's focus rule from landing here: this button leaves
    // the page, and a dialog that opens with the exit selected is one stray Enter from a redirect.
    attrs: { type: 'button', 'data-autofocus': 'skip' },
    on: {
      click: () => {
        if (button.disabled) return;
        button.disabled = true;
        text.textContent = pendingLabel;
        options.onSignIn();
      },
    },
  });

  button.appendChild(el('span', { class: 'lg-gsi__mark', html: GOOGLE_MARK_SVG }));
  button.appendChild(text);
  return button;
}

/**
 * Styles for the button, injected on first use.
 *
 * Not in `hud.css`, for the same reason `SIGN_IN_STYLES` is not: the sign-in wall renders before
 * the application — and therefore before anything that pulls the HUD stylesheet in — so a button
 * that depended on it would come up unstyled on the one screen where looking broken costs the most.
 *
 * Roboto is named first and then falls back the way Google's own published button does. The CSP
 * sets no `font-src`, so `fonts.gstatic.com` is refused and a webfont would have to be self-hosted;
 * until it is, Arial is the specified fallback rather than the app's `system-ui` stack, because the
 * button's proportions are set against Roboto's metrics.
 */
export const GOOGLE_SIGN_IN_STYLES = `
.lg-gsi {
  --lg-gsi-height: 40px;
  --lg-gsi-mark: 18px;
  --lg-gsi-font: 14px;
  --lg-gsi-pad: 12px;
  position: relative;
  display: inline-flex;
  align-items: center;
  /*
   * The mark sits on the leading edge and the label centres in what is left of the button, which
   * is how Google's own button lays out and why a wide one still reads as theirs. Centring the two
   * together instead leaves the logo floating in the middle of the field at any generous width.
   */
  justify-content: flex-start;
  box-sizing: border-box;
  height: var(--lg-gsi-height);
  min-width: 175px;
  max-width: 400px;
  padding: 0 var(--lg-gsi-pad);
  border: 1px solid #747775;
  border-radius: 4px;
  background-color: #fff;
  color: #1f1f1f;
  font-family: 'Roboto', arial, sans-serif;
  font-size: var(--lg-gsi-font);
  font-weight: 500;
  letter-spacing: 0.25px;
  cursor: pointer;
  overflow: hidden;
  transition: box-shadow 0.15s ease;
  -webkit-font-smoothing: antialiased;
}

.lg-gsi--large {
  --lg-gsi-height: 52px;
  --lg-gsi-mark: 24px;
  --lg-gsi-font: 16px;
  --lg-gsi-pad: 16px;
  border-radius: 6px;
}

.lg-gsi--block {
  display: flex;
  width: 100%;
}

/*
 * Google's states are an overlay rather than a second background colour, which is what keeps the
 * white surface behind the mark white — the guidelines do not permit tinting it.
 */
.lg-gsi::after {
  content: '';
  position: absolute;
  inset: 0;
  background-color: #303030;
  opacity: 0;
  transition: opacity 0.15s ease;
  pointer-events: none;
}

.lg-gsi:hover:not(:disabled)::after { opacity: 0.08; }
.lg-gsi:active:not(:disabled)::after { opacity: 0.12; }

.lg-gsi:hover:not(:disabled) {
  box-shadow: 0 1px 2px rgba(60, 64, 67, 0.3), 0 1px 3px 1px rgba(60, 64, 67, 0.15);
}

.lg-gsi:focus-visible {
  outline: 3px solid #a8c7fa;
  outline-offset: 2px;
}

.lg-gsi:disabled {
  cursor: default;
  border-color: rgba(31, 31, 31, 0.12);
  color: rgba(31, 31, 31, 0.38);
  box-shadow: none;
}

.lg-gsi:disabled .lg-gsi__mark {
  opacity: 0.38;
}

.lg-gsi__mark {
  display: block;
  flex: 0 0 auto;
  width: var(--lg-gsi-mark);
  height: var(--lg-gsi-mark);
  margin-right: var(--lg-gsi-pad);
}

.lg-gsi__mark svg {
  display: block;
  width: 100%;
  height: 100%;
}

.lg-gsi__label {
  flex-grow: 1;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
`;

let injected = false;

/** Put {@link GOOGLE_SIGN_IN_STYLES} in the document once, whichever entry point asks first. */
export function injectGoogleSignInStyles(): void {
  if (injected || typeof document === 'undefined') return;
  injected = true;
  const style = document.createElement('style');
  style.dataset.lgStyles = 'google-sign-in';
  style.textContent = GOOGLE_SIGN_IN_STYLES;
  document.head.appendChild(style);
}
